import React from 'react';
import { useAuthorization } from '../hooks/useAuthorization';
import { AuthDisabledProvider } from '../context/AuthDisabledContext';

/**
 * A declarative wrapper to show, hide, or disable components based on user authorization.
 *
 * It uses the correct pattern for each mode:
 * - 'hide' (default): It makes a rendering decision and returns null.
 * - 'disable': It wraps its children in a context to broadcast the disabled state.
 *
 * @param {object} props
 * @param {React.Component} props.component - The component that defines the abilities.
 * @param {string} props.action - The action the user wants to perform (e.g., 'read', 'update').
 * @param {'hide' | 'disable'} [props.mode='hide'] - How to handle unauthorized access.
 * @param {React.ReactNode} props.children - The content to protect.
 */
const Protected = ({ component, action, mode = 'hide', children }) => {
    const { can } = useAuthorization(component);

    if (can(action)) {
        // If the user is authorized, render the children as normal.
        return <>{children}</>;
    }

    if (mode === 'disable') {
        return (
            <AuthDisabledProvider value={true}>
                {children}
            </AuthDisabledProvider>
        );
    }

    // Otherwise (unauthorized and 'hide' mode), render nothing.
    return null;
};

export default Protected;
